import React from "react";
import get from "lodash/get";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";
import Typography from "@mui/material/Typography";
import { useCMSData } from "app/hooks/useCMSData";
import useMediaQuery from "@mui/material/useMediaQuery";
import { PageLoader } from "app/components/page-loader";
import { colors, customBreakpointsMax } from "app/theme";
import { useUserSubInfo } from "app/hooks/useUserSubInfo";
import { InfoTooltip } from "app/components/info-tooltip";
import { getSignInMethods } from "app/utils/signInButtons";
import { getCMSDataField } from "app/utils/getCMSDataField";
import CircularProgress from "@mui/material/CircularProgress";
import { useLocalStorage, useSessionStorage } from "react-use";
import ProfilePicture from "app/assets/images/profile-picture.png";
import { useStoreActions, useStoreState } from "app/state/store/hooks";
import { socialAuth, SocialAuthConnectionType } from "app/utils/socialAuth";
import { ReactComponent as LogOutIcon } from "app/assets/vectors/HeaderSignInViewLogOut.svg";
import { ReactComponent as HeaderAccount } from "app/assets/vectors/HeaderSignInViewAccount.svg";
import { HeaderViewCommonBlockItem } from "app/components/header/sub-components/expanded-view/data";
import { ReactComponent as YourFavourites } from "app/assets/vectors/HeaderProductViewYourFavourites.svg";

export const HeaderSignInView: React.FC = () => {
  const location = useLocation();
  const { subscriptionPlan } = useUserSubInfo();
  const cmsData = useCMSData({ returnData: true });
  const mobile = useMediaQuery(customBreakpointsMax.mobile);
  const { user, isAuthenticated, logout, isLoading, loginWithRedirect } =
    useAuth0();

  const [loginLoading, setLoginLoading] = React.useState(false);

  const setActiveStep = useSessionStorage("publisher-active-step", -1)[1];
  const [_, setAuthRedirectData, clearAuthRedirectData] = useLocalStorage(
    "auth-redirect-data",
    {
      step: -1,
      pathname: "/",
    }
  );

  const loginFlow = useStoreState(
    (state) => state.activeHeaderButton.loginFlow
  );

  const clearLoginFlow = useStoreActions(
    (actions) => actions.activeHeaderButton.clearLoginFlow
  );

  const loadingStoredUser = useStoreState((state) => state.user.loading);
  const clearStoredUser = useStoreActions((actions) => actions.user.clear);
  const storedUser = useStoreState((state) => get(state.user, "data.data", {}));
  const clearIntercomUser = useStoreActions(
    (actions) => actions.IntercomUser.clear
  );
  const clearFavourites = useStoreActions(
    (actions) => actions.GetBookmarks.clear
  );

  const handleSignInButtonClick =
    (type: SocialAuthConnectionType | "email-otp") => () => {
      setLoginLoading(true);
      setAuthRedirectData({ pathname: location.pathname, step: -1 });
      if (type === "email-otp") {
        loginWithRedirect({
          authorizationParams: {
            connection: "email",
            screen_hint: "login",
          },
        });
      } else {
        socialAuth(type, loginFlow.metadata);
      }
      clearLoginFlow();
    };

  const handleLogOutButtonClick = () => {
    clearStoredUser();
    clearIntercomUser();
    setActiveStep(-1);
    clearAuthRedirectData();
    clearFavourites();
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const view = React.useMemo(() => {
    if (isLoading || loadingStoredUser) {
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (isAuthenticated) {
      const profileImage = storedUser.picture ?? ProfilePicture;
      let groupName = get(storedUser, "group", "-");
      if (groupName.length === 0) {
        groupName = "-";
      }
      return (
        <React.Fragment>
          <Typography variant="h5" fontSize="14px" marginBottom="20px">
            Hello {get(storedUser, "name", "-").split(" ")[0]}. You are signed
            in.
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              justifyContent: "space-between",
              [customBreakpointsMax.mobile]: {
                flexDirection: "column",
                alignItems: "flex-start",
              },
            }}
          >
            <Box
              sx={{
                gap: "20px",
                display: "flex",
                flexDirection: "row",
                "> img": {
                  width: "62px",
                  height: "62px",
                  borderRadius: "50%",
                  border: `1px solid ${colors.secondary.iconGray}`,
                },
                [customBreakpointsMax.mobile]: {
                  flexDirection: "column",
                  alignItems: "flex-start",
                },
              }}
            >
              <img src={profileImage} alt="Profile" />
              <Box
                sx={{
                  gap: "20px",
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "row",
                  "> div": {
                    gap: "5px",
                    display: "flex",
                    minWidth: "120px",
                    flexDirection: "column",
                  },
                  [customBreakpointsMax.mobile]: {
                    display: "none",
                  },
                }}
              >
                <Box>
                  <Typography variant="h6" fontSize="12px" fontWeight="400">
                    {getCMSDataField(
                      cmsData,
                      "componentsHeader.organisationText",
                      "Organisation"
                    )}
                  </Typography>
                  <Typography variant="h5" fontSize="14px">
                    {groupName}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" fontSize="12px" fontWeight="400">
                    {getCMSDataField(
                      cmsData,
                      "componentsHeader.planText",
                      "Plan"
                    )}
                  </Typography>
                  <Typography
                    variant="h5"
                    fontSize="14px"
                    textTransform="capitalize"
                  >
                    {subscriptionPlan}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" fontSize="12px" fontWeight="400">
                    {getCMSDataField(
                      cmsData,
                      "componentsHeader.publisherIdentifierText",
                      "Publisher identifier"
                    )}
                  </Typography>
                  <Typography variant="h5" fontSize="14px">
                    {get(
                      storedUser,
                      "app_metadata.publisherInfo.publisher_iati_id",
                      "-"
                    )}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box>
              <Button
                startIcon={<LogOutIcon />}
                onClick={handleLogOutButtonClick}
                sx={{
                  gap: "10px",
                  height: "32px",
                  display: "flex",
                  fontSize: "12px",
                  fontWeight: "700",
                  borderRadius: "5px",
                  alignItems: "center",
                  textTransform: "none",
                  justifyContent: "center",
                  color: `${colors.primary.white} !important`,
                  background: colors.secondary.red,
                  ":hover": {
                    background: colors.secondary.darkRed,
                  },
                  "> .MuiButton-icon": {
                    margin: 0,
                  },
                  [customBreakpointsMax.mobile]: {
                    marginTop: "20px",
                  },
                }}
                data-cy="nav-log-out-button"
              >
                Sign Out
              </Button>
            </Box>
          </Box>
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        {!mobile && (
          <Typography variant="h5" fontSize="16px" marginBottom="15px">
            {getCMSDataField(
              cmsData,
              "componentsHeader.quickLoginText",
              "Quick Sign In Actions"
            )}
          </Typography>
        )}
        <InfoTooltip
          open={loginFlow.showTooltip}
          arrow
          placement="bottom"
          mode="light"
          width="350px"
          offsetY={30}
          info={
            <Box>
              <Typography fontWeight={"700"} fontSize="14px" marginBottom="2px">
                {loginFlow.tooltipTitle}
              </Typography>

              <Typography
                fontWeight={"400"}
                fontSize="14px"
                whiteSpace={"pre-line"}
              >
                {loginFlow.tooltipText}
              </Typography>
            </Box>
          }
        >
          <Box
            sx={{
              gap: "20px",
              display: "flex",
              width: "max-content",
              flexDirection: "row",
              [customBreakpointsMax.mobile]: {
                alignItems: "center",
                flexDirection: "column",
              },
              "> button": {
                height: "36px",
                display: "flex",
                fontSize: "16px",
                padding: "0 12px",
                fontWeight: "700",
                borderRadius: "5px",
                alignItems: "center",
                textTransform: "none",
                justifyContent: "center",
                color: colors.primary.white,
                background: colors.primary.blue,
                ":hover": {
                  background: colors.shades.blue[400],
                },
              },
            }}
          >
            {getSignInMethods(cmsData).map((method) => (
              <Button
                key={method.title}
                startIcon={method.icon}
                data-cy="sign-in-method-button"
                onClick={handleSignInButtonClick(method.type)}
              >
                {method.title}
              </Button>
            ))}
          </Box>
        </InfoTooltip>
      </React.Fragment>
    );
  }, [
    user,
    isLoading,
    isAuthenticated,
    loadingStoredUser,
    storedUser,
    loginFlow,
    mobile,
  ]);

  return (
    <React.Fragment>
      {loginLoading && <PageLoader />}
      <Grid
        container
        spacing={2}
        sx={{
          gap: "20px",
          display: "grid",
          gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
          [customBreakpointsMax.tablet]: {
            gap: "20px",
            marginLeft: 0,
            display: "flex",
            flexDirection: "column-reverse",
          },
          [customBreakpointsMax.mobile]: {
            marginTop: 0,
          },
        }}
      >
        <Grid
          gridColumn={{
            xs: "span 12",
            md: "span 6",
            lg: "span 3",
          }}
        >
          <HeaderViewCommonBlockItem
            title={getCMSDataField(
              cmsData,
              "componentsHeader.myAccountText",
              "My Account"
            )}
            icon={<HeaderAccount />}
            subtitle={getCMSDataField(
              cmsData,
              "componentsHeader.myAccountSubText",
              "Connect to your account & manage your settings."
            )}
            link="/account-profile"
            active={location.pathname === "/account-profile"}
          />
        </Grid>
        <Grid
          gridColumn={{
            xs: "span 12",
            md: "span 6",
            lg: "span 3",
          }}
        >
          <HeaderViewCommonBlockItem
            title="Your Favourites"
            icon={<YourFavourites />}
            link="/explorer/favourites"
            subtitle="Save activities, stay in the loop."
            active={location.pathname === "/explorer/favourites"}
          />
        </Grid>
        <Grid
          gridColumn={{
            xs: "span 12",
            md: "span 12",
            lg: "span 6",
          }}
          sx={{
            paddingLeft: "20px",
            borderLeft: `1px solid ${colors.secondary.iconGray}`,
            [customBreakpointsMax.tablet]: {
              paddingLeft: "0",
              borderLeft: "none",
            },
            [customBreakpointsMax.mobile]: {
              display: "flex",
              justifyContent: "center",
            },
          }}
        >
          {view}
        </Grid>
      </Grid>
    </React.Fragment>
  );
};
